import React from 'react';
import { Link } from 'react-router-dom';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { usePermissions } from '../hooks/usePermissions';

const AccessDenied = ({ message = "You don't have permission to access this page." }) => {
  const { hasPermission } = usePermissions();

  // Define available routes with their permissions
  const availableRoutes = [
    { path: '/dashboard', label: 'Dashboard', permission: 'dashboard.read' },
    { path: '/inventory', label: 'Inventory', permission: 'inventory.read' },
    { path: '/job-cards', label: 'Job Cards', permission: 'jobcards.read' },
    { path: '/customers', label: 'Customers', permission: 'customers.read' },
    { path: '/members', label: 'Members', permission: 'members.read' },
    { path: '/vendors', label: 'Vendors', permission: 'vendors.read' },
    { path: '/accounts', label: 'Accounts', permission: ['accounts.read', 'invoices.read', 'quotations.read', 'payments.read'] },
    { path: '/schedule', label: 'Schedule', permission: 'schedule.read' },
    { path: '/reports', label: 'Reports', permission: 'reports.read' },
    { path: '/crm', label: 'CRM', permission: 'crm.read' },
    { path: '/profile', label: 'Profile', permission: null } // Always accessible
  ];

  // Filter routes user has access to
  const accessibleRoutes = availableRoutes.filter(route => {
    if (!route.permission) return true;
    
    // Handle multiple permissions (any one is sufficient)
    if (Array.isArray(route.permission)) {
      return route.permission.some(perm => hasPermission(perm));
    }
    
    return hasPermission(route.permission);
  });

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-6 text-center">
        <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-red-500 mb-4" />
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
        <p className="text-gray-600 mb-6">{message}</p>
        
        {accessibleRoutes.length > 0 ? (
          <div className="space-y-2">
            <p className="text-sm text-gray-500 mb-3">You can access these modules:</p>
            {accessibleRoutes.slice(0, 3).map((route, index) => (
              <Link
                key={route.path}
                to={route.path}
                className={`block w-full py-2 px-4 rounded-md transition-colors ${
                  index === 0 
                    ? 'bg-blue-600 text-white hover:bg-blue-700' 
                    : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                }`}
              >
                {route.label}
              </Link>
            ))}
          </div>
        ) : (
          <Link
            to="/profile"
            className="block w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition-colors"
          >
            View Profile
          </Link>
        )}
      </div>
    </div>
  );
};

export default AccessDenied;